'use client';

import { cn } from '@repo/ui';
import { type JSX, useMemo } from 'react';
import {
  AudioIcon,
  KaraokeText,
  loadExampleAudioCached,
  splitWords,
  useActiveWordIndex,
  useKaraokeAudio,
} from '@/components/karaoke';

interface IKaraokeExampleCardProps {
  slug: string;
  exampleId: number;
  initialAudioUrl: string;
  text: string;
  language: string;
  translatedText?: string;
  transDir: 'ltr' | 'rtl';
  locale?: string;
}

export function KaraokeExampleCard({
  slug,
  exampleId,
  initialAudioUrl,
  text,
  language,
  translatedText,
  transDir,
  locale,
}: IKaraokeExampleCardProps): JSX.Element {
  const { audioRef, wordTimings, isPlaying, isLoading, toggle, audioHandlers } = useKaraokeAudio(
    () => loadExampleAudioCached(slug, exampleId),
  );
  const words = useMemo(() => splitWords(text), [text]);
  const active = useActiveWordIndex(audioRef, wordTimings, words);
  return (
    <button
      aria-label="Play example"
      className={cn(
        'block w-full cursor-pointer rounded-xl px-4 py-3 text-start transition-all duration-200',
        isPlaying
          ? 'bg-primary/10 ring-1 ring-primary/20'
          : 'bg-muted hover:bg-muted/70 hover:shadow-sm hover:ring-1 hover:ring-foreground/5',
      )}
      onClick={toggle}
      type="button"
    >
      <span className="block text-sm text-foreground leading-relaxed" dir="ltr" lang={language}>
        <AudioIcon className="me-1.5 translate-y-0.5" isLoading={isLoading} isPlaying={isPlaying} />
        <KaraokeText active={active} words={words} />
      </span>
      {translatedText && (
        <span className="mt-1 block text-xs text-muted-foreground" dir={transDir} lang={locale}>
          {translatedText}
        </span>
      )}
      <audio preload="none" ref={audioRef} src={initialAudioUrl} {...audioHandlers}>
        <track kind="captions" />
      </audio>
    </button>
  );
}
